import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../styles/AdCarousel.css';

const AdCarousel = () => {
  // カルーセル設定（内部埋め込み）
  const CAROUSEL_SETTINGS = {
    autoPlayInterval: 4000,
    showDots: true,
    pauseOnHover: false, // ホバー時の一時停止を無効化
    fadeEffect: true
  };

  // 状態管理
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [isVisible, setIsVisible] = useState(false); // 自動スライド実行中フラグ
  const [autoSlideReady, setAutoSlideReady] = useState(false); // 自動スライド準備完了フラグ
  
  // タッチ/スワイプ用の状態
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  // マウスドラッグ用の状態
  const [mouseStart, setMouseStart] = useState(null);
  const [mouseEnd, setMouseEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // カルーセルコンテナの参照
  const carouselRef = useRef(null);

  // Observer内でリアルタイム値を参照するためのref
  const autoSlideReadyRef = useRef(false);
  const isVisibleRef = useRef(false);

  // S3 URL構築
  const isProduction = window.location.hostname === 'schedule.stpr.com';
  const BASE_URL = isProduction
    ? 'https://schedule-share-json.s3.ap-northeast-1.amazonaws.com/carousel'
    : 'https://dev-schedule-share-json.s3.ap-northeast-1.amazonaws.com/carousel';

  const BANNERS_JSON_URL = `${BASE_URL}/banner-list.json`;

  // 画像URLの構築
  const getImageUrl = useCallback((filename) => `${BASE_URL}/banner/${filename}`, [BASE_URL]);

  // 期限チェック関数（日本時間対応・時間まで考慮）
  const filterActiveBanners = useCallback((banners) => {
    const now = new Date();

    return banners
      .filter((banner) => {
        // デフォルト値の設定
        const defaultStartDate = '2000-01-01 00:00:00';
        const defaultEndDate = '2100-01-01 00:00:00';

        // startDateの処理
        let startDateStr = banner.startDate || defaultStartDate;
        if (typeof startDateStr !== 'string' || !startDateStr.trim()) {
          startDateStr = defaultStartDate;
        }

        // endDateの処理
        let endDateStr = banner.endDate || defaultEndDate;
        if (typeof endDateStr !== 'string' || !endDateStr.trim()) {
          endDateStr = defaultEndDate;
        }

        // 日時文字列をDateオブジェクトに変換
        const startDateTime = new Date(startDateStr);
        const endDateTime = new Date(endDateStr);

        // 無効な日付の場合はデフォルト値を使用
        const finalStartDateTime = isNaN(startDateTime.getTime())
          ? new Date(defaultStartDate)
          : startDateTime;

        const finalEndDateTime = isNaN(endDateTime.getTime())
          ? new Date(defaultEndDate)
          : endDateTime;

        return finalStartDateTime <= now && now < finalEndDateTime;
      });
  }, []);

  // バナーデータ取得
  const fetchBannerData = useCallback(async () => {
    setLoading(true);
    try {
      // タイムスタンプを追加してブラウザキャッシュを回避
      const timestamp = Date.now();
      const response = await fetch(`${BANNERS_JSON_URL}?v=${timestamp}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      // 有効なバナーをフィルタリング（期限チェック + imageフィールドの存在チェック）
      const activeBanners = filterActiveBanners(data.banners || [])
        .filter(banner => banner.image); // imageがない場合は除外
      setBanners(activeBanners);

      // インデックスをリセット
      if (activeBanners.length > 0) {
        setCurrentIndex(0);
      }
    } catch (error) {
      setBanners([]);
    } finally {
      setLoading(false);
    }
  }, [BANNERS_JSON_URL, filterActiveBanners]);

  // 初回データ取得
  useEffect(() => {
    fetchBannerData();
  }, [fetchBannerData]);

  // ref値の同期（Observer内でリアルタイム値を参照するため）
  useEffect(() => {
    autoSlideReadyRef.current = autoSlideReady;
  }, [autoSlideReady]);

  useEffect(() => {
    isVisibleRef.current = isVisible;
  }, [isVisible]);

  // 日付カレンダーのDOM要素が存在するまで待機
  const waitForDateCalendar = useCallback(() => {
    return new Promise((resolve) => {
      const checkCalendar = () => {
        const calendarElement = document.querySelector('.date-picker-1');
        if (calendarElement) {
          resolve();
        } else {
          requestAnimationFrame(checkCalendar);
        }
      };

      checkCalendar();
    });
  }, []);

  // シンプルなカルーセル自動スライド初期化
  useEffect(() => {
    if (banners.length === 0 || !carouselRef.current) {
      return;
    }

    const initializeCarousel = async () => {
      // 1. 日付カレンダーのDOM要素が存在するまで待機
      await waitForDateCalendar();

      // 2. 1秒待機（DOMレンダリング完了を確実にするため）
      await new Promise(resolve => setTimeout(resolve, 1000));

      // 3. 準備完了フラグをセット
      setAutoSlideReady(true);

      // 4. シンプルなObserver開始
      startSimpleObserver();
    };

    initializeCarousel();

    return () => {
      const currentCarousel = carouselRef.current;
      if (currentCarousel && currentCarousel._observer) {
        currentCarousel._observer.unobserve(currentCarousel);
        currentCarousel._observer = null;
      }
    };
  }, [banners.length, waitForDateCalendar]);

  // シンプルなIntersection Observer
  const startSimpleObserver = useCallback(() => {
    const currentCarousel = carouselRef.current;
    if (!currentCarousel) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // 準備完了 && 画面内表示 && 未開始 = 自動スライド開始
          if (autoSlideReadyRef.current && entry.isIntersecting && !isVisibleRef.current) {
            setIsVisible(true);
          }
        });
      },
      {
        threshold: 0.01, // 1%以上表示されたら検知
        rootMargin: '0px'
      }
    );

    observer.observe(currentCarousel);
    // クリーンアップ関数を保存
    currentCarousel._observer = observer;
  }, []); // ref.currentを使用するため依存配列は空

  // 自動スライド機能（画面に表示されてから開始）
  useEffect(() => {
    if (banners.length <= 1 || isPaused || !isVisible) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === banners.length - 1 ? 0 : prevIndex + 1
      );
    }, CAROUSEL_SETTINGS.autoPlayInterval);

    return () => clearInterval(interval);
  }, [banners.length, isPaused, isVisible, CAROUSEL_SETTINGS.autoPlayInterval]);

  // ドットクリック処理
  const handleDotClick = (index) => {
    setCurrentIndex(index);
  };

  // ホバー処理
  const handleMouseEnter = () => {
    if (CAROUSEL_SETTINGS.pauseOnHover) {
      setIsPaused(true);
    }
  };

  const handleMouseLeave = () => {
    if (CAROUSEL_SETTINGS.pauseOnHover) {
      setIsPaused(false);
    }
  };

  // タッチ/スワイプ・マウスドラッグ処理
  const minSwipeDistance = 50; // 最小スワイプ距離

  // 共通のスライド切り替え処理
  const handleSlideChange = (startPos, endPos) => {
    if (!startPos || !endPos) return;
    
    const distance = startPos - endPos;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && banners.length > 1) {
      // 左スワイプ/ドラッグ：次のスライドへ
      setCurrentIndex((prevIndex) => 
        prevIndex === banners.length - 1 ? 0 : prevIndex + 1
      );
    }
    
    if (isRightSwipe && banners.length > 1) {
      // 右スワイプ/ドラッグ：前のスライドへ
      setCurrentIndex((prevIndex) => 
        prevIndex === 0 ? banners.length - 1 : prevIndex - 1
      );
    }
  };

  // タッチイベント
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    // タップ（スワイプではない）の場合はリンククリックを許可
    if (!touchEnd || Math.abs(touchStart - touchEnd) < 10) {
      // タップとみなす（移動距離が10px未満）
      return;
    }
    handleSlideChange(touchStart, touchEnd);
  };

  // マウスイベント
  const onMouseDown = (e) => {
    // リンク要素の場合はpreventDefaultしない
    const isLink = e.target.tagName === 'A' || e.target.closest('a');
    if (!isLink) {
      e.preventDefault(); // テキスト選択を防ぐ
    }
    setMouseEnd(null);
    setMouseStart(e.clientX);
    setIsDragging(true);
  };

  const onMouseMove = (e) => {
    if (!isDragging) return;
    setMouseEnd(e.clientX);
  };

  const onMouseUp = () => {
    if (isDragging) {
      // クリック（ドラッグではない）の場合はリンククリックを許可
      if (!mouseEnd || Math.abs(mouseStart - mouseEnd) < 10) {
        setIsDragging(false);
        return;
      }
      handleSlideChange(mouseStart, mouseEnd);
      setIsDragging(false);
    }
  };

  const onMouseLeaveWrapper = () => {
    if (isDragging) {
      handleSlideChange(mouseStart, mouseEnd);
      setIsDragging(false);
    }
  };

  // ローディング中
  if (loading) {
    return (
      <div className="ad-carousel-container">
        <div className="ad-carousel-loading">
          <p>広告を読み込み中...</p>
        </div>
      </div>
    );
  }

  // バナーが存在しない場合は何も表示しない
  if (banners.length === 0) {
    return null;
  }

  return (
    <div 
      ref={carouselRef}
      className="ad-carousel-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div 
        className={`ad-carousel-wrapper ${isDragging ? 'dragging' : ''} ${isDragging && mouseEnd ? 'actually-dragging' : ''}`}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeaveWrapper}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        {banners.map((banner, index) => (
          <div
            key={index}
            className={`ad-carousel-slide ${
              index === currentIndex ? 'active' : ''
            } ${CAROUSEL_SETTINGS.fadeEffect ? 'fade-effect' : ''}`}
          >
            {banner.link ? (
              <a 
                href={banner.link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="ad-carousel-link"
              >
                <img 
                  src={getImageUrl(banner.image)} 
                  alt={banner.alt || '広告バナー'}
                  className="ad-carousel-image"
                  loading="lazy"
                />
              </a>
            ) : (
              <div className="ad-carousel-link">
                <img 
                  src={getImageUrl(banner.image)} 
                  alt={banner.alt || '広告バナー'}
                  className="ad-carousel-image"
                  loading="lazy"
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ドットインジケーター */}
      {CAROUSEL_SETTINGS.showDots && banners.length > 1 && (
        <div className="ad-carousel-dots">
          {banners.map((_, index) => (
            <button
              key={index}
              className={`ad-carousel-dot ${
                index === currentIndex ? 'active' : ''
              }`}
              onClick={() => handleDotClick(index)}
              aria-label={`スライド ${index + 1} に移動`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdCarousel;
